import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getDefeatedCreditsAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";
const ORDEN_POR_ATRASO = 1;

// Un crédito por fila: sus montos se suman directo, sin `unaVezPorId`.
const dinero = (key, label, total = true) => ({
  key,
  label,
  align: "center",
  total,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

const COLUMNAS = [
  { key: "id", label: "#", align: "center", valor: (c) => c.id, excel: (c) => c.id },
  {
    key: "fechaEntrega",
    label: "F.Entrega",
    align: "center",
    valor: (c) => formatearFecha(c.fechaEntrega),
    excel: (c) => formatearFecha(c.fechaEntrega),
  },
  {
    key: "fullName",
    label: "Nombre Completo",
    align: "left",
    texto: true,
    valor: (c) => c.fullName,
  },
  dinero("montoEntregado", "Monto Entregado"),
  {
    key: "fechaVencimiento",
    label: "F.Vencimiento",
    align: "center",
    valor: (c) => formatearFecha(c.fechaVencimiento),
    excel: (c) => formatearFecha(c.fechaVencimiento),
  },
  {
    key: "atrasoDias",
    label: "Días de Atraso",
    align: "center",
    valor: (c) => c.atrasoDias,
    excel: (c) => c.atrasoDias,
  },
  // Sin columna "Atraso": en un crédito ya vencido todas sus cuotas están atrasadas, así
  // que el atraso del endpoint es siempre igual al saldo total (verificado: 205 créditos).
  dinero("saldoTotal", "Saldo Total"),
];

const DefeatedCredits = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { selectedGestor, selectedSortBy } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getDefeatedCreditsAsync(token, {
          selectedSortBy: parseInt(selectedSortBy),
          gestorId: selectedGestor === TODOS ? 0 : parseInt(selectedGestor),
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [token, selectedGestor, selectedSortBy, setIsLoading, manejarError]);

  // El servidor ya manda el nombre: también el de un gestor dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Créditos Vencidos"
      nombreArchivo="CreditosVencidos"
      parametros={[{ label: "Gestor Seleccionado", valor: gestorName }]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      ordenInicial={
        parseInt(selectedSortBy) === ORDEN_POR_ATRASO ? "atrasoDias" : "fullName"
      }
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default DefeatedCredits;
